import { useState, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { AccountingPreview } from "@/components/shared/AccountingPreview";
import { DatePickerField } from "@/components/shared/DatePickerField";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDisplayDate, todayInUruguay, tomorrowInUruguay, parseLocalDateOnly } from "@/lib/dateFormat";
import {
  invoiceCreationBlockedEmptyExplanationKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import { invoiceKeys } from "@/lib/queryKeys";
import {
  createCreditNote,
  getInvoice,
  type CreateCreditNoteRequest,
  type InvoiceDetailEnriched,
} from "@/services/invoiceService";

const DEFAULT_RATES: Record<string, number> = {
  vat_basic: 0.22,
  vat_minimum: 0.10,
};

/**
 * Derive tax category and rate from the original invoice lines.
 * Uses the first line's tax category; falls back to known default rates
 * when rate is missing (field is optional in TaxInfo).
 */
function deriveTaxFromInvoice(invoice: InvoiceDetailEnriched): {
  category: string;
  rate: number;
} {
  const lines = invoice.lines ?? [];
  if (lines.length === 0) return { category: "vat_basic", rate: 0.22 };
  const first = lines[0];
  const cat = first.tax?.category ?? "vat_basic";
  const raw = first.tax?.rate ?? DEFAULT_RATES[cat] ?? 0;
  // `tax.rate` is stored inconsistently across invoice types: portal invoices
  // use a fraction (0.22), subscription CFEs use a whole-number percent (22).
  // Normalize to a fraction so the net/IVA math (total / (1 + rate)) is correct
  // for both — otherwise a subscription-CFE credit note splits 1207.80 into
  // 52.51 + 1155.29 (an invalid ~2200% IVA) and DGI rejects it.
  const rate = raw > 1 ? raw / 100 : raw;
  return { category: cat, rate };
}

function buildCreditPayload(
  invoice: InvoiceDetailEnriched,
  totalAmount: number,
  description: string,
  issueDate: string,
  globalReference: boolean
): CreateCreditNoteRequest {
  const currency = (invoice.currency ?? "UYU") as "UYU" | "USD";
  const { category, rate } = deriveTaxFromInvoice(invoice);
  const subtotal = rate > 0
    ? Math.round((totalAmount / (1 + rate)) * 100) / 100
    : totalAmount;
  const taxAmount = rate > 0
    ? Math.round((totalAmount - subtotal) * 100) / 100
    : 0;
  return {
    currency,
    issueDate,
    receiverSnapshot: invoice.receiverSnapshot ?? { name: "", rut: "" },
    lines: [
      {
        lineNumber: 1,
        description: "Nota de crédito",
        ...(description && description.toLowerCase() !== "nota de crédito" ? { dscItem: description } : {}),
        quantity: 1,
        unitPrice: { currency, amount: subtotal },
        tax: { category: category as "vat_basic", rate, amount: taxAmount },
        lineTotal: { currency, amount: totalAmount },
      },
    ],
    totals: {
      subtotal: { currency, amount: subtotal },
      taxTotal: { currency, amount: taxAmount },
      total: { currency, amount: totalAmount },
    },
    ...(globalReference && {
      referenceGlobal: true,
      // RazonRef: reuse the note's own reason when the user gave one; otherwise
      // omit and let the backend fill a default that names the original CFE.
      ...(description && description.toLowerCase() !== "nota de crédito"
        ? { referenceReason: description }
        : {}),
    }),
  };
}

export function CreditNoteWizardPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState(1);
  const [creditAmount, setCreditAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [issueDate, setIssueDate] = useState<string>("");
  const [globalReference, setGlobalReference] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();

  useEffect(() => {
    if (!id || !invoicingBlocked) return;
    navigate(`/invoices/${id}`, { replace: true });
    const key = invoiceCreationBlockedEmptyExplanationKey(invoicingBlockedReason);
    if (key) toast(t(key), { id: "invoicing-blocked" });
  }, [id, invoicingBlocked, invoicingBlockedReason, navigate, t]);

  const { data: invoice, isLoading, isError } = useQuery({
    queryKey: invoiceKeys.detail(id ?? ""),
    queryFn: () => getInvoice(id!),
    enabled: Boolean(id),
  });

  const remainingBalance =
    typeof invoice?.remainingBalance === "number" ? invoice.remainingBalance : 0;

  useEffect(() => {
    if (!invoice) return;
    // A credit/debit note is emitted today — never backdate it to the original
    // invoice's date. Beyond being wrong on its own, a backdated NC makes DGI
    // validate the Referencia against its registro as of that older date, where
    // a same-day original may not yet be posted (→ E12 "referencia no existe").
    setIssueDate((prev) => prev || todayInUruguay());
    // Prefill the reason: lead with "Devolución" (recommended) and append the
    // original invoice's line descriptions so the note describes what's being
    // credited. Capped at 90 to stay within DGI's RazonRef limit (F-C6).
    setReason((prev) => {
      if (prev) return prev;
      const descs = [
        ...new Set(
          (invoice.lines ?? [])
            .map((l) => l.description?.trim())
            .filter((d): d is string => Boolean(d)),
        ),
      ].join(", ");
      const prefill = descs ? `Devolución — ${descs}` : "Devolución";
      return prefill.slice(0, 90);
    });
  }, [invoice?.id]);

  useEffect(() => {
    if (!invoice || remainingBalance <= 0) return;
    setCreditAmount((prev) => {
      if (prev.trim() !== "") return prev;
      const loc = i18n.language.startsWith("es") ? "es-UY" : "en-US";
      return remainingBalance.toLocaleString(loc, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    });
  }, [invoice?.id, remainingBalance, i18n.language]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateCreditNoteRequest) =>
      createCreditNote(id!, payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id ?? "") });
      toast.success(t("invoices.notes.credit.created", "Credit Note created. It will be sent to DGI."));
      navigate(`/invoices/${created.id}`, { replace: true });
    },
    onError: (err: { response?: { data?: { message?: string }; status?: number } }) => {
      const msg =
        err?.response?.data?.message ??
        (err as Error).message ??
        t("invoices.notes.credit.error", "Failed to create Credit Note.");
      toast.error(msg);
    },
  });

  const totalCreditedSoFar = typeof invoice?.totalCredited === "number" ? invoice.totalCredited : 0;
  const originalTotal =
    typeof invoice?.originalInvoiceTotal === "number" ? invoice.originalInvoiceTotal : 0;

  const amountNum = useMemo(() => {
    // Handle es-UY format: "5.332,25" → strip thousand dots, replace decimal comma
    const cleaned = creditAmount.replace(/\./g, "").replace(",", ".");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [creditAmount]);

  const cappedAmount = Math.min(amountNum, remainingBalance);
  const newRemaining = remainingBalance - cappedAmount;
  const amountError =
    amountNum > remainingBalance
      ? t("invoices.notes.credit.amountExceedsRemaining", "Amount cannot exceed remaining balance.")
      : amountNum <= 0 && step >= 2
        ? t("invoices.notes.credit.amountRequired", "Enter a valid amount.")
        : null;

  const canProceedStep2 = amountNum > 0 && amountNum <= remainingBalance;
  const canSubmitStep4 = confirmed;

  if (!id) {
    return (
      <div className="fx-content">
        <PageHeader title={t("invoices.notes.credit.title", "Issue Credit Note")} />
        <p className="text-sm text-muted-foreground">{t("invoices.detail.invalidId", "Invalid invoice")}</p>
      </div>
    );
  }

  if (invoicingBlocked) {
    return <LoadingPage />;
  }

  if (isLoading || !invoice) {
    return <LoadingPage />;
  }

  if (isError || invoice.status !== "sent" || (invoice as unknown as Record<string, unknown>).dgiStatus !== "confirmed") {
    return (
      <div className="fx-content">
        <PageHeader title={t("invoices.notes.credit.title", "Issue Credit Note")} onBack={() => navigate(`/invoices/${id}`)} />
        <p className="text-sm text-muted-foreground">
          {t("invoices.notes.credit.invalidContext", "This invoice cannot receive a Credit Note.")}
        </p>
      </div>
    );
  }

  const cfNumber = String(invoice.internalNumber ?? "");

  const handleSubmit = (): void => {
    const payload = buildCreditPayload(
      invoice,
      cappedAmount,
      reason.trim(),
      issueDate,
      globalReference
    );
    createMutation.mutate(payload);
  };

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("invoices.notes.credit.title", "Issue Credit Note")}
        description={t("invoices.notes.credit.stepOf", "Step {{current}} of 4", { current: step })}
        onBack={
          step === 1
            ? () => navigate(`/invoices/${id}`)
            : () => setStep((s) => s - 1)
        }
      />

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">
              {t("invoices.notes.credit.step1Title", "Context")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("invoices.notes.credit.step1Explanation", "A Credit Note reduces the amount owed on this invoice. The total cannot exceed the remaining balance.")}
            </p>
            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="text-muted-foreground">{t("invoices.detail.relatedDocuments.cfeNumber", "CFE number")}</dt>
                <dd className="font-medium">{cfNumber}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t("invoices.detail.issueDate", "Issue date")}</dt>
                <dd className="font-medium">
                  {invoice.issueDate
                    ? formatDisplayDate(invoice.issueDate, {
                        locale: i18n.language,
                        useCalendarDate: true,
                      })
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t("invoices.detail.receiver", "Receiver")}</dt>
                <dd className="font-medium">{invoice.receiverSnapshot?.name ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t("invoices.detail.financialSummary.remainingBalance", "Remaining balance")}</dt>
                <dd className="font-semibold text-foreground">
                  {invoice.currency ?? "UYU"} {remainingBalance.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </dd>
              </div>
            </dl>
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} className="transition-colors duration-150">
                {t("invoices.notes.continue", "Continue")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">
              {t("invoices.notes.credit.step2Title", "Credit details")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="credit-amount">
                {t("invoices.notes.credit.amountLabel", "Credit amount")} (max {invoice.currency ?? "UYU"} {remainingBalance.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
              </Label>
              <Input
                id="credit-amount"
                type="text"
                inputMode="decimal"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="0.00"
                className={amountError ? "border-destructive" : ""}
              />
              {amountError && (
                <p className="text-sm text-destructive">{amountError}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="credit-reason">{t("invoices.notes.credit.reasonOptional", "Reason or description (optional)")}</Label>
              <Input
                id="credit-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("invoices.notes.credit.reasonPlaceholder", "e.g. Return, discount")}
              />
            </div>
            <DatePickerField
              id="credit-issue-date"
              value={issueDate}
              onChange={setIssueDate}
              label={t("invoices.new.issueDate", "Issue date")}
              maxDate={parseLocalDateOnly(tomorrowInUruguay())}
            />
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <label htmlFor="credit-global-ref" className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  id="credit-global-ref"
                  checked={globalReference}
                  onCheckedChange={(v) => setGlobalReference(v === true)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  <span className="font-medium">
                    {t("invoices.notes.credit.globalReference", "Referencia global")}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {t(
                      "invoices.notes.credit.globalReferenceHelp",
                      "Usá esto solo si la DGI rechaza la nota porque no encuentra el comprobante de referencia. La razón de arriba se envía como motivo.",
                    )}
                  </span>
                </span>
              </label>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => navigate("/invoices")} className="transition-colors duration-150">
                {t("pages.common.cancel", "Cancel")}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)} className="transition-colors duration-150">
                  {t("pages.common.back", "Back")}
                </Button>
                <Button onClick={() => setStep(3)} disabled={!canProceedStep2} className="transition-colors duration-150">
                  {t("invoices.notes.continue", "Continue")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <>
          <AccountingPreview
            currency={invoice.currency ?? "UYU"}
            originalTotal={originalTotal}
            totalCreditedSoFar={totalCreditedSoFar}
            thisAmount={cappedAmount}
            newRemainingBalance={newRemaining}
            noteType="credit"
            taxBreakdown={(() => {
              const { rate } = deriveTaxFromInvoice(invoice);
              if (rate <= 0) return undefined;
              const sub = Math.round((cappedAmount / (1 + rate)) * 100) / 100;
              return { subtotal: sub, taxAmount: Math.round((cappedAmount - sub) * 100) / 100, taxRate: rate };
            })()}
          />
          <Card>
            <CardContent className="flex justify-between pt-6">
              <Button variant="ghost" onClick={() => navigate("/invoices")} className="transition-colors duration-150">
                {t("pages.common.cancel", "Cancel")}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)} className="transition-colors duration-150">
                  {t("pages.common.back", "Back")}
                </Button>
                <Button onClick={() => setStep(4)} className="transition-colors duration-150">
                  {t("invoices.notes.continue", "Continue")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">
              {t("invoices.notes.credit.step4Title", "Confirmation")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("invoices.notes.credit.step4Warning", "This document will be sent to DGI and cannot be edited or deleted.")}
            </p>
            <div className="flex items-start gap-2">
              <Checkbox
                id="credit-confirm"
                checked={confirmed}
                onCheckedChange={(c) => setConfirmed(c === true)}
              />
              <Label htmlFor="credit-confirm" className="text-sm font-normal cursor-pointer">
                {t("invoices.notes.credit.confirmCheckbox", "I understand and wish to issue this Credit Note.")}
              </Label>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => navigate("/invoices")} className="transition-colors duration-150">
                {t("pages.common.cancel", "Cancel")}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(3)} className="transition-colors duration-150">
                  {t("pages.common.back", "Back")}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmitStep4 || createMutation.isPending}
                  className="transition-colors duration-150"
                >
                  {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("invoices.notes.credit.submit", "Issue Credit Note")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
